<template>
  <div class="w-full max-w-7xl mx-auto px-[4vw] pb-12">
    <Head>
      <Title>{{ $t('restaurant.meta.title', { name: restaurant?.name, city: restaurant?.city }) }}</Title>
      <Meta name="description" :content="$t('restaurant.meta.description', { name: restaurant?.name, city: restaurant?.city, tagline: restaurant?.shortDescription })" />
      <Meta name="og:image" :content="useCloudinaryImage(restaurant?.cover, 1200, 630)" />
    </Head>
    <div class="lg:mt-8 relative">
      <RestaurantHero :restaurant="restaurant" />
      <ClientOnly>
        <div class="mt-8" v-if="!restaurant.bookable">
          <RestaurantClosedInfo :restaurant="restaurant" />
        </div>
      </ClientOnly>
      <RestaurantNavigation :restaurant="restaurant" />
      <div class="max-w-prose grid gap-3" :id="useSlugify($t('restaurant.navigation.description'))">
        <div>
          <h1 class="font-heading font-bold text-4xl sm:text-5xl md:text-6xl" v-html="restaurant?.name" />
          <p class="font-bold text-lg leading-6 md:text-xl md:leading-7" v-html="restaurant?.shortDescription" />
          <!-- <p class="font-bold text-lg leading-6" v-html="restaurant?.shortDescription" /> -->
        </div>
        <ul class="flex flex-wrap gap-2 md:gap-4 _my-2" v-if="tagsGroup3?.length">
          <li class="bg-sky text-ocean text-sm px-2 py-1 rounded" v-for="tag in tagsGroup3" :key="tag.id" v-html="tag.label" />
        </ul>
        <div class="grid gap-4">
          <p class="leading-6" v-html="restaurant?.longDescription" />
          <!-- <p class="leading-5 text-sm" v-html="restaurant?.longDescription" /> -->
          <AtomRestaurantLocation 
            :name="restaurant?.address" 
            :address="restaurant?.city"
          /> 
          <div class="flex items-center gap-4 text-sm max-w-sm" v-if="restaurant.bookable">
            <button class="button-coral w-1/2" @click="useOpenWidget(restaurant.hash)">{{ $t('shared.bookTable') }}</button>
            <button class="button-coral-outline w-1/2" @click="useOpenWidget(restaurant.hash)" v-if="restaurant.settings?.giftcardsEnabled">{{ $t('shared.buyGiftcard') }}</button>
          </div>
        </div>
      </div>
      <div class="max-w-prose grid gap-8 md:gap-12 my-8">
        <div class="grid gap-4" :id="useSlugify($t('restaurant.navigation.goodToKnow'))" v-if="tagsGroup7.length || tagsGroup2.length || restaurant?.details.goodToKnow || restaurant?.details.typicalFood || restaurant?.details.typicalBeverage">
          <RestaurantBlockHeader 
            :label="$t('restaurant.details.label')"
            :heading="$t('restaurant.details.heading', restaurant)"
          />
          <div class="bg-mist rounded p-4 grid gap-5">
            <div class="grid gap-1" v-if="tagsGroup7.length">
              <h3 class="font-bold" v-html="$t('restaurant.details.visitingFor.heading', { name: restaurant?.name })" />
              <ul class="grid gap-1 list-disc ml-4 leading-6">
                <li class="text-sm md:text-base" v-for="tag in tagsGroup7" :key="tag.id" v-html="tag.label" />
              </ul>
            </div>
            <div class="grid gap-1" v-if="tagsGroup2.length">
              <h3 class="font-bold" v-html="$t('restaurant.details.targeting.heading', { name: restaurant?.name })" />
              <div class="text-sm md:text-base" v-html="tagsGroup2.map(tag => tag?.label)?.join(', ')" />
            </div>
            <div class="grid gap-1" v-if="restaurant?.details.goodToKnow">
              <h3 class="font-bold" v-html="$t('restaurant.details.goodToKnow.heading', { name: restaurant?.name })" />
              <p class="text-sm md:text-base" v-html="restaurant?.details.goodToKnow" />
            </div>
            <div class="grid gap-1" v-if="restaurant?.details.typicalFood">
              <h3 class="font-bold" v-html="$t('restaurant.details.typicalFood.heading', { name: restaurant?.name })" />
              <p class="text-sm md:text-base" v-html="restaurant?.details.typicalFood" />
            </div>
            <div class="grid gap-1" v-if="restaurant?.details.typicalBeverage">
              <h3 class="font-bold" v-html="$t('restaurant.details.typicalBeverage.heading', { name: restaurant?.name })" />
              <p class="text-sm md:text-base" v-html="restaurant?.details.typicalBeverage" />
            </div>
          </div>
        </div>
        <div class="grid gap-4" :id="useSlugify($t('restaurant.navigation.menu'))" v-if="restaurant.menus?.length">
          <RestaurantBlockHeader 
            :label="$t('restaurant.menu.label')"
            :heading="$t('restaurant.menu.heading', restaurant)"
          />
          <div class="bg-mist rounded p-4 grid gap-5">
            <a class="block flex items-center gap-2" v-for="menu in restaurant.menus" :key="`menu-${menu.id}`" :href="menu.source?.url" target="_blank">
              <div class="w-5">
                <AtomIcon name="file-pdf" v-if="menu.source?.type === 'pdf'" />
                <AtomIcon name="arrow-up-right-from-square" v-else />
              </div>
              <div>
                <div class="font-bold">{{ menu.name }}</div>
                <div class="text-sm" v-if="menu.description">{{ menu.description }}</div>
              </div>
            </a>
          </div>
        </div>
        <div class="grid gap-4" :id="useSlugify($t('restaurant.navigation.images'))" v-if="restaurant.images?.length > 1">
          <RestaurantBlockHeader 
            :label="$t('restaurant.images.label')"
            :heading="$t('restaurant.images.heading', restaurant)"
          />
          <RestaurantGallery :restaurant="restaurant" />
        </div>
        <!-- <div class="grid gap-4" :id="useSlugify($t('restaurant.navigation.booking'))" v-if="restaurant.bookable">
          <RestaurantBlockHeader 
            :label="$t('restaurant.meals.label')"
            :heading="$t('restaurant.meals.heading', restaurant)"
          />
          <RestaurantMeals :restaurant="restaurant" />
        </div> -->
        <div class="grid gap-4" :id="useSlugify($t('restaurant.navigation.openingHours'))" v-if="restaurant.openingHours?.length">
          <RestaurantBlockHeader 
            :label="$t('restaurant.openingHours.label')"
            :heading="$t('restaurant.openingHours.heading', restaurant)"
          />
          <RestaurantOpeningHours :restaurant="restaurant" v-if="restaurant?.openingHours?.length" />
        </div>
        <div class="grid gap-4" :id="useSlugify($t('restaurant.navigation.contact'))">
          <RestaurantBlockHeader 
            :label="$t('restaurant.contact.label')"
            :heading="$t('restaurant.contact.heading', restaurant)"
          />
          <RestaurantContact :restaurant="restaurant" />
        </div>
        <div class="grid gap-4" :id="useSlugify($t('restaurant.navigation.map'))">
          <RestaurantBlockHeader 
            :label="$t('restaurant.map.label')"
            :heading="$t('restaurant.map.heading', restaurant)"
          />
          <ClientOnly>
            <RestaurantMap :restaurant="restaurant" />
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const route = useRoute()

// Fetch restaurant data
const { data } = await useFetch(`/restaurants/${route.params?.slug}`, { 
  key: `restaurants-${route.path}`,
  baseURL: useEnv('apiBase')
})

// Store restaurant
const restaurant = computed(() => data.value?.data)
const restaurantId = restaurant?.value?.id
const restaurantSlug = restaurant?.value?.slug

// Flatten tags
const flatTags = computed(() => useRestaurantTags(restaurant?.value.tags))

// Get tags based on groups
const tagsGroup2 = computed(() => flatTags.value?.filter(tag => tag.groupId === 2) || [])
const tagsGroup3 = computed(() => flatTags.value?.filter(tag => tag.groupId === 3) || [])
const tagsGroup7 = computed(() => flatTags.value?.filter(tag => tag.groupId === 7) || [])

// Redirect to slug if slug is restaurant ID
if (restaurantId && restaurantSlug !== route.params?.slug) {
  navigateTo({ 
    name: 'restaurants-slug', 
    params: { slug: restaurantSlug }
  },
  { redirectCode: 301 })
}

// Show widget on mounted if #boka as hash
onMounted(() => {
  setTimeout(() => {
    if (route?.hash === '#boka') {
      useUpdateQuery({ widget: restaurant.value?.hash })
    }
  }, 250)
})

// Show 404 if no restaurant is found
if (!restaurantId || !restaurantSlug) {
  throw createError({
    statusCode: 404,
    message: 'not found',
    fatal: true
  })
}
</script>
